import * as React from "react";
import { useAppTheme } from "../../theme/AppThemeContext.jsx";
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useRef, useEffect, useState } from "react";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import EventNoteIcon from "@mui/icons-material/EventNote";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import BuildIcon from "@mui/icons-material/Build";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LoopIcon from "@mui/icons-material/Loop";

// ---------- SVG sprite icon helper ----------
function SvgSpriteIcon({
  spriteUrl,
  symbolId,
  size = 18,
  className,
}) {
  return (
    <svg
      className={className ?? "MuiSvgIcon-root MuiSvgIcon-fontSizeMedium"}
      focusable="false"
      aria-hidden="true"
      viewBox="0 0 24 24"
      style={{ width: size, height: size }}
    >
      <use href={`${spriteUrl}#${symbolId}`} />
    </svg>
  );
}

// ---------- Inline SVG Icons ----------
function SvgInlineClockIcon({ size = 16, color = "currentColor" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" fill={color} />
      <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z" fill={color} />
    </svg>
  );
}

// ---------- Time Gutter Component ----------
function TimeGutter({ startMin, endMin, pxPerMin, visualTops }) {
  
  const hours = [];
  const firstHour = Math.ceil(startMin / 60);
  const lastHour = Math.floor(endMin / 60);

  // Create hourly marks for equal distribution - include ALL whole hours
  for (let h = firstHour; h <= lastHour; h++) hours.push(h);

  // Calculate equal spacing for time distribution
  const totalMinutes = endMin - startMin;
  
  // Create time marks - ensure all whole hours are included
  const timeMarks = [];
  
  // Add all whole hours first
  for (let h = firstHour; h <= lastHour; h++) {
    const hourInMinutes = h * 60;
    if (hourInMinutes >= startMin && hourInMinutes <= endMin) {
      timeMarks.push({ 
        hour: h, 
        minute: 0, 
        totalMinutes: hourInMinutes,
        isWholeHour: true
      });
    }
  }
  
  // Add additional marks for large time ranges (optional half-hours)
  const hourInterval = totalMinutes > 480 ? 120 : totalMinutes > 240 ? 60 : 30;
  if (hourInterval < 60) {
    // Add half-hour marks for shorter ranges
    for (let min = startMin; min <= endMin; min += 30) {
      const hour = Math.floor(min / 60);
      const minute = min % 60;
      
      // Skip if this is already a whole hour mark
      if (minute === 0) continue;
      
      timeMarks.push({ 
        hour, 
        minute, 
        totalMinutes: min,
        isWholeHour: false
      });
    }
  }
  
  // Sort by time
  timeMarks.sort((a, b) => a.totalMinutes - b.totalMinutes);
  const formatTime = (hour, minute) => {
    const am = hour < 12;
    const h12 = ((hour + 11) % 12) + 1;
    const mm = String(minute).padStart(2, "0");
    return `${h12}:${mm} ${am ? "AM" : "PM"}`;
  };

  return (
    <Box sx={{ position: "relative", height: (endMin - startMin) * pxPerMin }}>
      {/* Time labels positioned at container boundaries */}
      {timeMarks.map((mark, index) => {
        const y = ((mark.totalMinutes - startMin) / totalMinutes) * ((endMin - startMin) * pxPerMin);
        return (
          <Typography
            key={`${mark.hour}-${mark.minute}`}
            sx={{
              position: "absolute",
              top: index === 0 ? 8 : (index === timeMarks.length - 1 ? 'auto' : y - 8),
              bottom: index === timeMarks.length - 1 ? 8 : 'auto',
              left: 12,
              fontSize: 12,
              color: "rgba(0,0,0,0.45)",
              fontWeight: mark.isWholeHour ? 600 : 400, // Bold whole hours
              opacity: mark.isWholeHour ? 1 : 0.7, // Stronger for whole hours
              ...(index > 0 && index < timeMarks.length - 1 && {
                top: y - 8,
                bottom: 'auto',
              }),
            }}
          >
            {formatTime(mark.hour, mark.minute)}
          </Typography>
        );
      })}
    </Box>
  );
}

// ---------- Main Day View Timeline ----------
export function DayViewTimeline({
  spriteUrl,
  rows,
  onToggleDone,
  onTaskClick,
  containerBg = "white",
}) {
  
  const { tokens } = useAppTheme();
  const bg = "rgb(248, 248, 248)";
  const textDark = "rgb(20, 20, 20)";
  const muted = "rgb(133, 133, 133)";
  const border = "1px solid rgba(0,0,0,0.08)";

  const taskRows = rows.filter(r => r.kind === "task");

  return (
    <Box
      id="day-view-content"
      sx={{
        width: "100%",
        position: "relative",
        background: containerBg,
        borderRadius: "24px 24px 0 0",
        boxShadow: "0px 2px 16px rgba(0,0,0,0.08)",
        overflowY: "auto",
        flex: "1 1 0%",
        minHeight: 0,
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 3 }}>
        <Box sx={{ maxWidth: 980, mx: "auto" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {rows.map((r, idx) =>
              r.kind === "task" ? (
                <TaskRowPanel
                  key={r.id}
                  spriteUrl={spriteUrl}
                  task={r}
                  taskIndex={taskRows.findIndex(t => t.id === r.id)} // stable within tasks
                  taskCount={taskRows.length}
                  onToggleDone={onToggleDone}
                  onClick={onTaskClick}
                />
              ) : (
                <GapPromptRow key={r.id} row={r} />
              )
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ---------- Row: Task (single row, 3 columns) ----------
function TaskRowPanel({
  spriteUrl,
  task,
  taskIndex = 0,
  taskCount = 1,
  onToggleDone,
  onClick,
}) {
  const { tokens } = useAppTheme();
  const muted = "rgb(133, 133, 133)";
  const textDark = "rgb(20, 20, 20)";
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every 30 seconds for real-time status updates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000); // Update every 30 seconds

    return () => clearInterval(timer);
  }, []);

  // Circle/pill logic
  const isFirst = taskIndex === 0;
  const isLast = taskIndex === taskCount - 1;
  const isCircle = isFirst || isLast;

  const durationMin = diffMinutes(task.startTime, task.endTime);

  // Height in px for the icon holder: match your ref feel.
  // Keep it visually nice: min 54px for pills; cap at 120px.
  const pillHeight = clamp(24 + durationMin * 1.6, 54, 120);

  // Circle size (first/last)
  const circleSize = 52;
  const nodeW = isCircle ? circleSize : 30;
  const nodeH = isCircle ? circleSize : pillHeight;
  const nodeR = isCircle ? 999 : 18;

  // ---------- Task Status Evaluation ----------
const evaluateTaskStatus = (task) => {
  const now = currentTime;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Get task date and times
  const taskDate = task.date ? new Date(task.date) : today;
  const taskDateOnly = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
  
  // Check if task is for today
  const isToday = taskDateOnly.getTime() === today.getTime();
  
  if (!isToday || !task.startTime || task.allDay) {
    return { status: 'not_today', isOngoing: false, remainingTime: null };
  }
  
  // Parse start and end times
  const [startHour, startMin] = task.startTime.split(':').map(Number);
  const [endHour, endMin] = task.endTime ? task.endTime.split(':').map(Number) : [startHour + 1, startMin];
  
  const taskStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), startHour, startMin);
  const taskEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), endHour, endMin);
  
  // Evaluate status
  if (now < taskStart) {
    return { status: 'upcoming', isOngoing: false, remainingTime: null };
  } else if (now >= taskStart && now < taskEnd) {
    const remainingMs = taskEnd - now;
    const remainingMinutes = Math.floor(remainingMs / 60000);
    return { 
      status: 'ongoing', 
      isOngoing: true, 
      remainingTime: remainingMinutes 
    };
  } else {
    return { status: 'elapsed', isOngoing: false, remainingTime: null };
  }
};

const formatRemainingTime = (minutes) => {
  if (minutes <= 0) return '0m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

// Modern color theme
const MODERN_COLORS = {
  primary: {
    main: '#6366f1',      // Modern indigo
    light: '#818cf8',     // Light indigo
    dark: '#4f46e5',      // Dark indigo
    elapsed: '#94a3b8',   // Muted slate for elapsed tasks
    ongoing: '#f59e0b',   // Amber for ongoing tasks
  },
  accent: {
    coral: '#f97316',     // Modern orange/coral
    teal: '#14b8a6',      // Modern teal
    emerald: '#10b981',   // Modern emerald
    rose: '#f43f5e',      // Modern rose
    purple: '#a855f7',    // Modern purple
    amber: '#f59e0b',     // Modern amber
  }
};

// Enhanced color system with time-based evaluation
const getTaskColors = (task) => {
  if (task.completed) {
    return { bg: "rgb(100,116,139)", fg: "#fff" };
  }
  
  const taskStatus = evaluateTaskStatus(task);
  
  if (taskStatus.status === 'elapsed') {
    // Use muted color for elapsed tasks
    return { bg: MODERN_COLORS.primary.elapsed, fg: "#fff" };
  }
  
  if (taskStatus.status === 'ongoing') {
    // Use amber color for ongoing tasks
    return { bg: MODERN_COLORS.primary.ongoing, fg: "#fff" };
  }
  
  if (task.color) {
    return { bg: task.color, fg: "#fff" };
  }
  
  // Default modern colors based on task title/content
  const defaultColors = [
    { bg: MODERN_COLORS.primary.main, fg: "#fff" },     // Indigo
    { bg: MODERN_COLORS.accent.teal, fg: "#fff" },      // Teal
    { bg: MODERN_COLORS.accent.emerald, fg: "#fff" },   // Emerald
    { bg: MODERN_COLORS.accent.rose, fg: "#fff" },       // Rose
    { bg: MODERN_COLORS.accent.purple, fg: "#fff" },     // Purple
    { bg: MODERN_COLORS.accent.amber, fg: "#fff" },      // Amber
  ];
  
  const colorIndex = task.id ? task.id.charCodeAt(task.id.length - 1) % defaultColors.length : 0;
  return defaultColors[colorIndex];
};

  const { bg, fg } = getTaskColors(task);
  const taskStatus = evaluateTaskStatus(task);

  // Format time labels
  const formatTimeLabel = (task) => {
    if (task.startTime && task.endTime) {
      const duration = calculateDuration(task.startTime, task.endTime);
      return `${task.startTime} - ${task.endTime} (${duration})`;
    }
    return task.startTime || '';
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "auto 44px 1fr", sm: "68px 44px 1fr" },
        columnGap: 6,
        alignItems: "stretch",
        py: 1.25, // tighter => less dead space
        px: { xs: 0.5, sm: 1 },
        borderRadius: 3,
        cursor: "pointer",
        transition: "background-color 140ms ease, transform 140ms ease",
        "&:hover": {
          backgroundColor: "rgba(0,0,0,0.04)", // like your reference
          transform: "translateY(-1px)",
        },
        ...(taskStatus.status === 'ongoing' && {
          backgroundColor: 'rgba(245, 158, 11, 0.02)', // Very subtle amber tint
          '&:hover': {
            backgroundColor: 'rgba(245, 158, 11, 0.06)', // Slightly stronger on hover
          },
        }),
      }}
    >
      {/* Column 1: start/end time */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // pins top/bottom
          alignSelf: "stretch",            // match row height
          height: "100%",                  // take full row height
          pl: { xs: 0.5, sm: 1 },
          py: 0.5,                         // small breathing room
        }}
      >
        <Typography sx={{ fontSize: 12, color: muted, lineHeight: 1, display: { xs: 'none', sm: 'block' } }}>
          {task.startTime || ""}
        </Typography>

        <Typography sx={{ fontSize: 12, color: muted, lineHeight: 1, display: { xs: 'none', sm: 'block' } }}>
          {task.endTime || ""}
        </Typography>
        
        {/* Single line time for mobile */}
        <Typography sx={{ fontSize: 12, color: muted, display: { xs: 'block', sm: 'none' } }}>
          {task.startTime && task.endTime ? `${task.startTime}–${task.endTime}` : (task.startTime || '')}
        </Typography>
      </Box>

      {/* Column 2: icon + vertical timeline connector */}
      <Box
        sx={{
          position: "relative",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          alignSelf: "stretch",            // ensure it always matches the row height
          "&::before": {
            content: '""',
            position: "absolute",
            top: -22,
            bottom: -22,
            left: "50%",
            transform: "translateX(-50%)",
            width: 2,
            opacity: task.completed ? 0.25 : (taskStatus.status === 'ongoing' ? 0.9 : 0.75),
            background: task.completed
              ? "rgba(100,116,139,0.25)"
              : (taskStatus.status === 'ongoing' 
                ? "rgba(245, 158, 11, 0.85)" // Brighter amber for ongoing
                : `rgba(${tokens.primaryHover.slice(4, -1)}, 0.75)`),
            borderRadius: 2,
            ...(taskStatus.status === 'ongoing' && {
              boxShadow: '0 0 8px rgba(245, 158, 11, 0.3)', // Subtle glow
            }),
          },
        }}
      >
        <Box
          sx={{
            width: nodeW,
            height: nodeH,
            borderRadius: nodeR,
            background: bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: taskStatus.status === 'ongoing' 
              ? "0px 10px 18px rgba(245, 158, 11, 0.25), 0 0 0 3px rgba(245, 158, 11, 0.15), inset 0 1px 0 rgba(255,255,255,0.25)" // Enhanced amber glow + inset highlight
              : "0px 10px 18px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.25)", // Standard shadow + inset highlight
            position: "relative",
            zIndex: 1,
            transition: "transform 140ms ease, box-shadow 140ms ease",
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: taskStatus.status === 'ongoing'
                ? "0px 14px 24px rgba(245, 158, 11, 0.35), 0 0 0 4px rgba(245, 158, 11, 0.2), inset 0 1px 0 rgba(255,255,255,0.3)"
                : "0px 14px 24px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.3)",
            },
            ...(taskStatus.status === 'ongoing' && {
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: "0px 10px 18px rgba(245, 158, 11, 0.25), 0 0 0 3px rgba(245, 158, 11, 0.15), inset 0 1px 0 rgba(255,255,255,0.25)",
                },
                '50%': {
                  boxShadow: "0px 10px 18px rgba(245, 158, 11, 0.35), 0 0 0 5px rgba(245, 158, 11, 0.25), inset 0 1px 0 rgba(255,255,255,0.3)",
                },
                '100%': {
                  boxShadow: "0px 10px 18px rgba(245, 158, 11, 0.25), 0 0 0 3px rgba(245, 158, 11, 0.15), inset 0 1px 0 rgba(255,255,255,0.25)",
                },
              },
            }),
          }}
        >
          <Box sx={{ color: fg, display: "flex", alignItems: "center" }}>
            {task.spriteSymbolId ? (
              <SvgSpriteIcon
                spriteUrl={spriteUrl}
                symbolId={task.spriteSymbolId}
                size={20}
              />
            ) : (
              // best-effort semantic icon
              <Box sx={{ color: fg, display: "flex", alignItems: "center" }}>
                {pickFallbackIcon(task)}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Column 3: times + recurring + title + radio */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr auto", sm: "1fr 44px" },
          alignItems: "center",
          minWidth: 0,
        }}
      >
        {/* Left content */}
        <Box
          onClick={() => onClick?.(task.id)}
          role="button"
          tabIndex={0}
          sx={{
            cursor: "pointer",
            minWidth: 0,
            pr: { xs: 1, sm: 2 },
            display: { xs: "flex", sm: "block" },
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 0.5, sm: 0.75 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ fontSize: 12, color: muted }}>
              {formatTimeLabel(task)}
            </Typography>

            {task.isRecurring && (
              <LoopIcon
                sx={{
                  fontSize: 16,
                  color: muted,
                  transform: "translateY(1px)",
                }}
                aria-hidden="true"
                title="Recurring"
              />
            )}

            {/* NOW badge for ongoing tasks */}
            {taskStatus.status === 'ongoing' && (
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#fff',
                  backgroundColor: MODERN_COLORS.primary.ongoing,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 999,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  animation: 'badgePulse 3s infinite',
                  '@keyframes badgePulse': {
                    '0%': {
                      transform: 'scale(1)',
                      boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                    },
                    '95%': {
                      transform: 'scale(1)',
                      boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                    },
                    '96%': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 2px 6px rgba(245, 158, 11, 0.4)',
                    },
                    '98%': {
                      transform: 'scale(1.02)',
                      boxShadow: '0 2px 5px rgba(245, 158, 11, 0.35)',
                    },
                    '100%': {
                      transform: 'scale(1)',
                      boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                    },
                  },
                }}
              >
                NOW
              </Box>
            )}
          </Box>

          <Box
            sx={{
              mt: 0.25,
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: 16,
              fontWeight: 800,
              color: task.completed ? muted : textDark,
              textDecoration: task.completed ? "line-through" : "none",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onClick?.(task);
            }}
          >
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {task.title}
            </Box>
            {task.hasOverlap && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  px: 0.75,
                  py: 0.2,
                  borderRadius: 1,
                  background: 'rgba(245,158,11,0.15)',
                  color: '#b45309',
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1.4,
                }}
              >
                OVERLAP
              </Box>
            )}
            {taskStatus.isOngoing && taskStatus.remainingTime !== null && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  fontSize: 12,
                  fontWeight: 600,
                  color: MODERN_COLORS.primary.ongoing,
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                }}
              >
                {formatRemainingTime(taskStatus.remainingTime)} left
              </Box>
            )}
          </Box>
        </Box>

        {/* Far-right: radio button (done) - custom toggle */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, justifyContent: "flex-end" }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              const newCompletedState = !task.completed;
              onToggleDone?.(task.id, newCompletedState);
            }}
            sx={{ p: 0 }}
            title={task.completed ? "Mark undone" : "Mark done"}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: `2px solid ${task.completed ? "rgba(100,116,139,0.85)" : "rgba(239,68,68,0.75)"}`,
                backgroundColor: task.completed ? "rgba(100,116,139,0.85)" : "transparent",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: task.completed ? "rgba(100,116,139,0.95)" : "rgba(239,68,68,0.12)",
                  boxShadow: "0 0 0 6px rgba(226,85,102,0.10)",
                },
              }}
            >
              {task.completed && (
                <svg
                  width={10}
                  height={10}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ 
                    display: 'block',
                    animation: 'checkmarkScale 0.3s ease-out',
                    '@keyframes checkmarkScale': {
                      '0%': {
                        transform: 'scale(0)',
                        opacity: '0',
                      },
                      '50%': {
                        transform: 'scale(1.2)',
                        opacity: '0.8',
                      },
                      '100%': {
                        transform: 'scale(1)',
                        opacity: '1',
                      },
                    },
                  }}
                >
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="white"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </Box>
          </IconButton>
        </Box>

        {/* Mobile inline radio button */}
        <Box sx={{ display: { xs: "flex", sm: "none" }, alignItems: "center", ml: "auto" }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              const newCompletedState = !task.completed;
              onToggleDone?.(task.id, newCompletedState);
            }}
            sx={{ p: 0 }}
            title={task.completed ? "Mark undone" : "Mark done"}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: `2px solid ${task.completed ? "rgba(100,116,139,0.85)" : "rgba(239,68,68,0.75)"}`,
                backgroundColor: task.completed ? "rgba(100,116,139,0.85)" : "transparent",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: task.completed ? "rgba(100,116,139,0.95)" : "rgba(239,68,68,0.12)",
                  boxShadow: "0 0 0 4px rgba(226,85,102,0.10)",
                },
              }}
            >
              {task.completed && (
                <svg
                  width={9}
                  height={9}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ 
                    display: 'block',
                    animation: 'checkmarkScale 0.3s ease-out',
                    '@keyframes checkmarkScale': {
                      '0%': {
                        transform: 'scale(0)',
                        opacity: '0',
                      },
                      '50%': {
                        transform: 'scale(1.2)',
                        opacity: '0.8',
                      },
                      '100%': {
                        transform: 'scale(1)',
                        opacity: '1',
                      },
                    },
                  }}
                >
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="white"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </Box>
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

// ---------- Row: Gap prompt ----------
function GapPromptRow({ row }) {
  const { tokens } = useAppTheme();
  const muted = "rgb(133, 133, 133)";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "auto 44px 1fr", sm: "68px 44px 1fr" },
        columnGap: 6,
        alignItems: "center",
        py: 0.25,
      }}
    >
      <Box />
      <Box
        sx={{
          position: "relative",
          height: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -6,
            bottom: -6,
            left: "50%",
            transform: "translateX(-50%)",
            width: 2,
            borderLeft: "2px dashed rgba(0,0,0,0.18)",
          },
        }}
      />
      <Box sx={{ pl: 0.5 }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 13 }}>
          <span style={{ color: muted }}>Free time</span>
          <span style={{ color: tokens.primary, fontWeight: 600 }}>{row.durationText}</span>
          <span style={{ color: muted }}>—</span>
          <Box
            onClick={row.onClick}
            role="button"
            tabIndex={0}
            sx={{
              color: "rgb(100, 116, 139)",
              fontWeight: 500,
              cursor: "pointer",
              textDecoration: "none",
              transition: "background-color 140ms ease, text-decoration 140ms ease",
              px: 0.5,
              py: 0.25,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                textDecoration: "underline",
                textDecorationColor: "rgba(100, 116, 139, 0.6)",
                textDecorationThickness: 1,
                textUnderlineOffset: 2,
              },
              "&:focus-visible": {
                outline: "2px solid rgba(100, 116, 139, 0.5)",
                outlineOffset: 1,
              },
            }}
          >
            Add a task
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ---------- Helper Functions ----------
function diffMinutes(startTime, endTime) {
  if (!startTime || !endTime) return 0;
  return Math.max(0, parseTime(endTime) - parseTime(startTime));
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

// Best-effort semantic icon mapping based on title
function pickFallbackIcon(task) {
  const t = (task.title || "").toLowerCase();
  if (t.includes("wake")) return <WbSunnyIcon fontSize="small" />;
  if (t.includes("devotion") || t.includes("pray") || t.includes("meditat"))
    return <SelfImprovementIcon fontSize="small" />;
  if (t.includes("walk") || t.includes("jog") || t.includes("run"))
    return <DirectionsWalkIcon fontSize="small" />;
  if (t.includes("plan") || t.includes("schedule"))
    return <EventNoteIcon fontSize="small" />;
  if (t.includes("piano") || t.includes("music"))
    return <MusicNoteIcon fontSize="small" />;
  if (t.includes("build") || t.includes("land") || t.includes("project"))
    return <BuildIcon fontSize="small" />;

  return <CheckCircleOutlineIcon fontSize="small" />;
}

function calculateDuration(startTime, endTime) {
  if (!startTime || !endTime) return '';
  
  const start = parseTime(startTime);
  const end = parseTime(endTime);
  const diff = end - start;
  const hours = Math.floor(diff / 60);
  const minutes = diff % 60;
  
  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${minutes}m`;
  }
}

function parseTime(timeStr) {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

export default DayViewTimeline;
